import { useState, useCallback } from 'react';
import apiClient from '../api/client';
import useAuthToken from './useAuthToken';

export const useTemplates = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const token = useAuthToken();

  const createTemplate = useCallback(async ({ name, html_content, text_content, design_json, automation_id }) => {
    setIsSaving(true);
    setError(null);

    try {
      console.log('Creating template with name:', name);
      console.log('Payload:', {
        name,
        html_content_length: html_content?.length || 0,
        text_content_length: text_content?.length || 0,
        design_json_length: design_json?.length || 0,
        automation_id
      });
      
      const response = await apiClient.post('/templates', {
        name,
        html_content: html_content || '',
        text_content: text_content || '',
        design_json: design_json || '',
        automation_id: automation_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Template created successfully:', response.data);
      return response.data;
    } catch (err) {
      console.error('Error saving template:', err);
      
      // Enhanced error logging
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      }
      
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [token]);

  const updateTemplate = useCallback(async (id, { name, html_content, text_content, design_json }) => {
    setIsSaving(true);
    setError(null);

    try {
      console.log(`Updating template ${id} with data:`, { 
        name, 
        html_content_length: html_content?.length || 0, 
        text_content_length: text_content?.length || 0,
        design_json_length: design_json?.length || 0
      });
      
      const response = await apiClient.patch(`/templates/${id}`, {
        name,
        html_content: html_content || '',
        text_content: text_content || '',
        design_json: design_json || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Template updated successfully:', response.data);
      return response.data;
    } catch (err) {
      console.error(`Error updating template ${id}:`, err);
      
      // Enhanced error logging
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      }
      
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [token]);

  const getTemplate = useCallback(async (id) => {
    setIsSaving(true);
    setError(null);
    
    try {
      console.log(`Fetching template ${id}`);
      const response = await apiClient.get(`/templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Template fetched successfully:', {
        id: response.data.id,
        name: response.data.name,
        html_content_length: response.data.html_content?.length || 0
      });
      
      return response.data;
    } catch (err) {
      console.error(`Error fetching template ${id}:`, err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [token]);

  const getAllTemplates = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    
    try {
      console.log('Fetching all templates');
      const response = await apiClient.get('/templates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log(`Fetched ${response.data.length} templates`);
      return response.data;
    } catch (err) {
      console.error('Error fetching all templates:', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [token]);

  return {
    createTemplate,
    updateTemplate,
    getTemplate,
    getAllTemplates,
    isSaving,
    error,
  };
};

export default useTemplates;